// Shared SVG sparkline coordinate helpers. Extracted from SparklineBar.vue
// (2026-09-28, Japan fund page work, Task 11) so NiySparklineBar can reuse
// the same value -> pixel scaling without duplicating it. SparklineBar's own
// rendered output (per-segment colored <line> elements) is unchanged by this
// extraction -- only the coordinate math moved here; the per-segment stroke
// coloring stays in SparklineBar.vue since it's specific to that component's
// up/down semantics.

export interface SparkPoint { x: number; y: number }

/**
 * Scale a series of values into pixel coordinates across [0,width] x
 * [0,height], with y=0 at the top (SVG convention) so larger values sit
 * higher on the chart.
 *
 * `opts.min`/`opts.max` let callers extend the y-domain beyond the values
 * themselves -- e.g. NiySparklineBar includes the threshold-band edges in
 * the domain so the band stays visible even when the price series never
 * reaches it.
 */
export function sparkPoints(
  values: number[],
  width: number,
  height: number,
  opts?: { min?: number; max?: number },
): SparkPoint[] {
  if (values.length === 0) return []
  const min = opts?.min ?? Math.min(...values)
  const max = opts?.max ?? Math.max(...values)
  const range = (max - min) || 1
  const xStep = values.length > 1 ? width / (values.length - 1) : 0
  return values.map((v, i) => ({
    x: i * xStep,
    y: height - ((v - min) / range) * height,
  }))
}

/** Build an SVG path `d` string ("M x,y L x,y ...") from scaled points. */
export function sparkPathD(points: SparkPoint[]): string {
  if (points.length === 0) return ''
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
}

export interface SparkSegment { x1: number; y1: number; x2: number; y2: number }

/**
 * Consecutive-pair segments from scaled points. Callers (e.g. SparklineBar)
 * layer per-segment coloring (direction-based stroke) on top.
 */
export function sparkSegmentsFromPoints(points: SparkPoint[]): SparkSegment[] {
  const segs: SparkSegment[] = []
  for (let i = 0; i < points.length - 1; i++) {
    segs.push({ x1: points[i].x, y1: points[i].y, x2: points[i + 1].x, y2: points[i + 1].y })
  }
  return segs
}

// ── Per-segment direction colouring (FOF home redesign 2026-11-02, spec §7) ──
// Moved here verbatim from SparklineBar.vue; GoldSparklineBar.vue and
// GgeClMiniChart.vue carried byte-equivalent copies. One implementation now.
export const UP_GOOD = '#4caf50'   // green
export const DOWN_BAD = '#f44336'  // red

export interface ColouredSparkSegment extends SparkSegment { stroke: string }

/**
 * Bloomberg-style sparkline: one segment per adjacent pair, stroked green when
 * the next value is higher (red when lower); `upIsGood=false` swaps the two for
 * "risk/pain" series (VIX, GVZ, yields, GBPUSD). Empty under two values.
 */
export function sparkSegments(values: number[], width: number, height: number, upIsGood: boolean): ColouredSparkSegment[] {
  if (values.length < 2) return []
  const up = upIsGood ? UP_GOOD : DOWN_BAD
  const down = upIsGood ? DOWN_BAD : UP_GOOD
  const points = sparkPoints(values, width, height)
  return sparkSegmentsFromPoints(points).map((s, i) => ({
    ...s,
    stroke: values[i + 1] > values[i] ? up : down,
  }))
}

// ── Coarse colouring for the FOF mini charts (Paolo 2026-11-03) ──────────────
// Per-pair colouring flickers red/green on every tick of an 80 px chart. The FOF
// home charts colour each segment by where its END sits against the START of the
// series (above = up colour, below = down colour, equal = keep the colour), and
// then collapse the shortest colour runs until there are at most `maxTransitions`
// colour changes — ideally one: red to green, or green to red.

/** One colour per segment, from the end value's side of the series' first value — or of `base` when given (the FOF
 * anchored / K_t charts colour against the strike value, Paolo 2026-11-03); an equal value keeps the colour. */
export function coarseSegmentColours(values: number[], upIsGood: boolean, maxTransitions = 4, base?: number | null): string[] {
  if (values.length < 2) return []
  const up = upIsGood ? UP_GOOD : DOWN_BAD
  const down = upIsGood ? DOWN_BAD : UP_GOOD
  if (base == null || !Number.isFinite(base)) base = values[0]
  let cur = values[values.length - 1] >= base ? up : down   // only used while the series sits exactly on its start
  const colours: string[] = []
  for (let i = 1; i < values.length; i++) {
    if (values[i] > base) cur = up
    else if (values[i] < base) cur = down
    colours.push(cur)
  }
  // Runs of one colour; merge the shortest run into its neighbours (which share the other colour) until few enough remain.
  const runs: { colour: string; len: number }[] = []
  for (const c of colours) {
    if (runs.length && runs[runs.length - 1].colour === c) runs[runs.length - 1].len++
    else runs.push({ colour: c, len: 1 })
  }
  const limit = Math.max(0, Math.floor(maxTransitions)) + 1
  while (runs.length > limit) {
    let k = 0
    for (let i = 1; i < runs.length; i++) if (runs[i].len < runs[k].len) k = i
    const into = k > 0 ? k - 1 : k + 1
    runs[into].len += runs[k].len
    runs.splice(k, 1)
    if (k > 0 && k < runs.length && runs[k].colour === runs[k - 1].colour) {   // the two neighbours now touch
      runs[k - 1].len += runs[k].len
      runs.splice(k, 1)
    }
  }
  return runs.flatMap(r => Array<string>(r.len).fill(r.colour))
}

/** `sparkSegments` with the coarse colouring above. Empty under two values. */
export function coarseSparkSegments(values: number[], width: number, height: number, upIsGood: boolean, maxTransitions = 4, base?: number | null): ColouredSparkSegment[] {
  if (values.length < 2) return []
  const colours = coarseSegmentColours(values, upIsGood, maxTransitions, base)
  return sparkSegmentsFromPoints(sparkPoints(values, width, height)).map((s, i) => ({ ...s, stroke: colours[i] }))
}
